import { Component, OnInit, inject, Output, EventEmitter, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { BrandService } from '../../services/brand.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';

@Component({
    selector: 'app-brand-list',
    standalone: true,
    imports: [CommonModule, NgSelectModule, FormsModule, ListCardComponent, MobileFooterComponent],
    templateUrl: './brand-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './brand-list.component.css'
})
export class BrandListComponent implements OnInit {

    brands: any[] = [];
    filteredBrands: any[] = [];
    filterCode: number | null = null;
    searchTerm = '';

    isLoading = true;
    rows = Array.from({ length: 5 }, (_, i) => i + 1);

    public order = true;
    public orderColName = 'name';
    selectedId = signal<any>(null);

    private brandService = inject(BrandService);
    public appSettings = inject(AppSettings);

    @Output() selectEvent = new EventEmitter<any>();

    ngOnInit(): void {
        this.loadBrands();
    }

    loadBrands() {
        this.isLoading = true;
        this.brandService.get().subscribe({
            next: (res) => {
                this.brands = (res || []).map((b: any) => ({
                    ...b,
                    label: `${b.code} - ${b.name}`
                }));
                this.applyFilters();
                this.isLoading = false;
            },
            error: (err) => {
                console.error(err);
                this.isLoading = false;
            }
        });
    }

    searchBrands = (term: string, item: any) => {
        const q = String(term || '').trim().toLowerCase();
        if (!q) return true;
        return String(item?.name || '').toLowerCase().includes(q)
            || String(item?.code ?? '').toLowerCase().includes(q)
            || String(item?.label || '').toLowerCase().includes(q);
    };

    compareCodes = (a: any, b: any) => {
        if (a == null || b == null) return a == b;
        return Number(a) === Number(b);
    };

    onSearch(event: { term?: string } | string) {
        this.searchTerm = typeof event === 'string' ? event : (event?.term || '');
        this.applyFilters();
    }

    onFilterChange(code: number | null) {
        this.filterCode = code != null && code !== undefined && String(code) !== ''
            ? Number(code)
            : null;
        if (this.filterCode != null) {
            this.searchTerm = '';
            const brand = this.brands.find((b) => Number(b.code) === this.filterCode);
            if (brand) this.selectBrand(brand);
        }
        this.applyFilters();
    }

    applyFilters() {
        const term = this.searchTerm.trim().toLowerCase();
        this.filteredBrands = this.brands.filter((b) => {
            if (!term) return true;
            return String(b.name || '').toLowerCase().includes(term)
                || String(b.code ?? '').toLowerCase().includes(term);
        });
        this.applyOrder();
    }

    applyOrder() {
        this.filteredBrands.sort((a, b) => {
            const valA = a[this.orderColName];
            const valB = b[this.orderColName];
            if (typeof valA === 'string' && typeof valB === 'string') {
                return this.order ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return this.order ? (valA ?? 0) - (valB ?? 0) : (valB ?? 0) - (valA ?? 0);
        });
    }

    isSelected(brand: any): boolean {
        const current = this.selectedId();
        if (current == null || brand?.code == null) return false;
        return Number(current) === Number(brand.code);
    }

    nextBrandCode(): number {
        return this.brands.reduce((max, b) => Math.max(max, Number(b.code) || 0), 0) + 1;
    }

    selectBrand(brand: any) {
        this.selectedId.set(brand.code);
        this.selectEvent.emit(brand);
    }

    upsertBrand(brand: any) {
        if (!brand) return;
        const mapped = { ...brand, label: `${brand.code} - ${brand.name}` };
        const idx = this.brands.findIndex((b) =>
            Number(b.code) === Number(mapped.code)
            || (mapped.id != null && b.id === mapped.id)
        );
        if (idx >= 0) {
            this.brands[idx] = { ...this.brands[idx], ...mapped };
        } else {
            this.brands = [...this.brands, mapped];
        }
        this.applyFilters();
    }

    removeBrand(code: any) {
        this.brands = this.brands.filter((b) => Number(b.code) !== Number(code));
        if (Number(this.selectedId()) === Number(code)) this.selectedId.set(null);
        this.applyFilters();
    }
}
